import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Loader2, Download } from 'lucide-react';

const EMPTY_SALE = { referral_code: '', customer_email: '', product: '', amount_aed: '', note: '' };

export const AdminAffiliates = () => {
  const [partners, setPartners] = useState([]);
  const [sales, setSales] = useState({ sales: [], total_commission_aed: 0, unpaid_commission_aed: 0 });
  const [loading, setLoading] = useState(true);
  const [codeInputs, setCodeInputs] = useState({});
  const [saleForm, setSaleForm] = useState(EMPTY_SALE);
  const [addingSale, setAddingSale] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/affiliate/partners`),
        axios.get(`${API}/admin/affiliate/sales`),
      ]);
      setPartners(pRes.data.partners || []);
      setSales(sRes.data);
    } catch { toast.error('Failed to load affiliate data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleApprove = async (p) => {
    try {
      const code = (codeInputs[p.id] || '').trim();
      await axios.patch(`${API}/admin/affiliate/partners/${p.id}`, { status: 'active', ...(code ? { referral_code: code } : {}) });
      toast.success(`${p.full_name} approved`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
  };

  const handleStatusChange = async (p, status) => {
    try {
      await axios.patch(`${API}/admin/affiliate/partners/${p.id}`, { status });
      toast.success(`Status updated to ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  const handleMarkPaid = async (saleId) => {
    try {
      await axios.patch(`${API}/admin/affiliate/sales/${saleId}`, { commission_status: 'paid' });
      toast.success('Marked as paid');
      fetchAll();
    } catch { toast.error('Failed to update sale'); }
  };

  const handleAddSale = async (e) => {
    e.preventDefault();
    if (!saleForm.referral_code || !saleForm.customer_email || !saleForm.product || !saleForm.amount_aed) {
      toast.error('Fill code, customer email, product and amount'); return;
    }
    setAddingSale(true);
    try {
      await axios.post(`${API}/admin/affiliate/sales`, { ...saleForm, amount_aed: parseFloat(saleForm.amount_aed) });
      toast.success('Sale recorded');
      setSaleForm(EMPTY_SALE);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add sale'); }
    finally { setAddingSale(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliate/sales/export-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'affiliate_sales.csv'; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-affiliates-section">
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Affiliate Partners ({partners.length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b"><tr className="text-left"><th className="pb-2">Name</th><th className="pb-2">Email</th><th className="pb-2">WhatsApp</th><th className="pb-2">Status</th><th className="pb-2">Code</th><th className="pb-2">Actions</th></tr></thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} className="border-b" data-testid={`partner-row-${p.id}`}>
                  <td className="py-2 font-medium">{p.full_name}</td>
                  <td className="py-2 text-xs text-gray-600">{p.email}</td>
                  <td className="py-2 text-xs">{p.whatsapp}</td>
                  <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-green-100 text-green-800' : p.status === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'}`}>{p.status}</span></td>
                  <td className="py-2 text-xs font-mono">{p.referral_code || '-'}</td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      {p.status === 'pending' && (
                        <>
                          <input placeholder="code (optional)" value={codeInputs[p.id] || ''} onChange={e => setCodeInputs({ ...codeInputs, [p.id]: e.target.value })}
                            className="h-7 w-24 rounded border border-gray-300 px-2 text-xs" data-testid={`partner-code-input-${p.id}`} />
                          <button onClick={() => handleApprove(p)} className="text-xs text-green-600 hover:underline" data-testid={`approve-partner-${p.id}`}>Approve</button>
                        </>
                      )}
                      {p.status === 'active' && <button onClick={() => handleStatusChange(p, 'suspended')} className="text-xs text-red-500 hover:underline">Suspend</button>}
                      {p.status === 'suspended' && <button onClick={() => handleStatusChange(p, 'active')} className="text-xs text-green-600 hover:underline">Reactivate</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {partners.length === 0 && <p className="text-center text-gray-400 py-8">No affiliate applications yet.</p>}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Affiliate Sales — Total AED {sales.total_commission_aed} (Unpaid: AED {sales.unpaid_commission_aed})</h2>
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-affiliate-sales-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
        </div>
        <form onSubmit={handleAddSale} className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg" data-testid="add-sale-form">
          <input placeholder="Referral Code" value={saleForm.referral_code} onChange={e => setSaleForm({ ...saleForm, referral_code: e.target.value })} className="h-8 w-32 rounded border border-gray-300 px-2 text-xs" data-testid="sale-code-input" />
          <input placeholder="Customer Email" value={saleForm.customer_email} onChange={e => setSaleForm({ ...saleForm, customer_email: e.target.value })} className="h-8 w-40 rounded border border-gray-300 px-2 text-xs" data-testid="sale-email-input" />
          <input placeholder="Product" value={saleForm.product} onChange={e => setSaleForm({ ...saleForm, product: e.target.value })} className="h-8 w-32 rounded border border-gray-300 px-2 text-xs" data-testid="sale-product-input" />
          <input type="number" placeholder="Amount AED" value={saleForm.amount_aed} onChange={e => setSaleForm({ ...saleForm, amount_aed: e.target.value })} className="h-8 w-24 rounded border border-gray-300 px-2 text-xs" data-testid="sale-amount-input" />
          <Button size="sm" type="submit" className="btn-uae-red h-8 text-xs" disabled={addingSale} data-testid="add-sale-btn">{addingSale ? '...' : 'Add Sale'}</Button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b"><tr className="text-left"><th className="pb-2">Code</th><th className="pb-2">Partner</th><th className="pb-2">Customer</th><th className="pb-2">Product</th><th className="pb-2">Amount</th><th className="pb-2">Commission</th><th className="pb-2">Status</th></tr></thead>
            <tbody>
              {sales.sales?.map(s => (
                <tr key={s.id} className="border-b">
                  <td className="py-2 font-mono text-xs">{s.referral_code}</td>
                  <td className="py-2 text-xs">{s.partner_name}</td>
                  <td className="py-2 text-xs">{s.customer_email}</td>
                  <td className="py-2 text-xs">{s.product}</td>
                  <td className="py-2 text-xs">AED {s.amount_aed}</td>
                  <td className="py-2 text-xs">AED {s.commission_aed}</td>
                  <td className="py-2">
                    {s.commission_status === 'paid' ? <span className="text-xs text-green-600">Paid</span> : (
                      <button onClick={() => handleMarkPaid(s.id)} className="text-xs text-blue-600 hover:underline" data-testid={`mark-paid-${s.id}`}>Mark Paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!sales.sales || sales.sales.length === 0) && <p className="text-center text-gray-400 py-8">No sales recorded yet.</p>}
        </div>
      </Card>
    </div>
  );
};
